/**
 * AquaDropship Theme JavaScript
 * Shopify OS 2.0 Theme for US/UK Dropshipping
 */

(function () {
  'use strict';

  // ==========================================================================
  // DOM UTILITIES
  // ==========================================================================
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  // ==========================================================================
  // QUANTITY SELECTOR
  // ==========================================================================
  class QuantitySelector {
    constructor(container) {
      this.container = container;
      this.input = $('input', container);
      this.decreaseBtn = $('[data-decrease]', container);
      this.increaseBtn = $('[data-increase]', container);
      this.min = parseInt(this.input.min) || 1;
      this.max = parseInt(this.input.max) || 9999;

      this.bindEvents();
    }

    bindEvents() {
      this.decreaseBtn?.addEventListener('click', () => this.decrease());
      this.increaseBtn?.addEventListener('click', () => this.increase());
      this.input?.addEventListener('change', () => this.validate());
    }

    get value() {
      return parseInt(this.input.value) || this.min;
    }

    set value(val) {
      this.input.value = Math.max(this.min, Math.min(this.max, val));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    decrease() {
      this.value = this.value - 1;
    }

    increase() {
      this.value = this.value + 1;
    }

    validate() {
      this.value = this.value;
    }
  }

  // Initialize all quantity selectors
  $$('.quantity-selector').forEach(el => new QuantitySelector(el));

  // ==========================================================================
  // MOBILE MENU
  // ==========================================================================
  class MobileMenu {
    constructor() {
      this.menu = $('#mobile-menu');
      this.openBtn = $('[data-mobile-menu-open]');
      this.closeBtn = $('[data-mobile-menu-close]');
      this.overlay = $('.mobile-menu__overlay');

      if (!this.menu) return;
      this.bindEvents();
    }

    bindEvents() {
      this.openBtn?.addEventListener('click', () => this.open());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.menu.classList.contains('is-open');
    }

    open() {
      this.menu.classList.add('is-open');
      this.overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.menu.classList.remove('is-open');
      this.overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }
  }

  new MobileMenu();

  // ==========================================================================
  // CART DRAWER
  // ==========================================================================
  class CartDrawer {
    constructor() {
      this.drawer = $('#cart-drawer');
      this.overlay = $('.cart-drawer__overlay');
      this.closeBtn = $('[data-cart-close]');

      if (!this.drawer) return;
      this.bindEvents();
    }

    bindEvents() {
      // Open cart drawer when clicking cart icon
      $$('[data-cart-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Listen for cart updates
      document.addEventListener('cart:updated', () => this.refresh());
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    open() {
      this.drawer.classList.add('is-open');
      this.overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    async refresh() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();
        this.updateCartCount(cart.item_count);
        updateCartMini(cart);
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }

    updateCartCount(count) {
      $$('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    }
  }

  new CartDrawer();

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active;
    return currency ? `${currency} ${amount}` : `$${amount}`;
  };

  const escapeHtml = (value) => {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const updateCartCountEls = (count) => {
    $$('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  };

  const updateCartMini = (cart) => {
    const mini = $('[data-cart-mini]');
    if (!mini) return;

    const inner = $('.cart-mini__inner', mini);
    if (!inner) return;

    if (cart.item_count > 0) {
      const itemsHtml = cart.items.slice(0, 3).map((item, index) => {
        const title = escapeHtml(item.product_title || item.title || 'Item');
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<span class="cart-mini__variant">${escapeHtml(item.variant_title)}</span>`
          : '';
        const image = item.image
          ? `<img src="${item.image}" alt="${title}" width="50" height="50" loading="lazy">`
          : '';
        const line = index + 1;
        const removeUrl = `${window.Shopify?.routes?.root || '/'}cart/change?line=${line}&quantity=0`;

        return `
          <div class="cart-mini__item">
            <a href="${item.url}" class="cart-mini__image">
              ${image}
            </a>
            <div class="cart-mini__details">
              <a href="${item.url}" class="cart-mini__item-title">${title}</a>
              ${variant}
              <div class="cart-mini__price">
                <span>${item.quantity} x ${formatMoney(item.final_price)}</span>
              </div>
            </div>
            <a href="${removeUrl}" class="cart-mini__remove" aria-label="Remove">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </a>
          </div>
        `;
      }).join('');

      const moreHtml = cart.items.length > 3
        ? `<p class="cart-mini__more">+ ${cart.items.length - 3} more items</p>`
        : '';

      inner.innerHTML = `
        <div class="cart-mini__header">
          <span class="cart-mini__title">Shopping Cart (${cart.item_count})</span>
        </div>
        <div class="cart-mini__items">
          ${itemsHtml}
          ${moreHtml}
        </div>
        <div class="cart-mini__footer">
          <div class="cart-mini__subtotal">
            <span>Subtotal:</span>
            <span>${formatMoney(cart.total_price)}</span>
          </div>
          <a href="${window.Shopify?.routes?.root || '/'}cart" class="btn btn--secondary btn--full btn--sm">View Cart</a>
          <a href="${window.Shopify?.routes?.root || '/'}checkout" class="btn btn--primary btn--full btn--sm">Checkout</a>
        </div>
      `;
    } else {
      inner.innerHTML = `
        <div class="cart-mini__empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
          </svg>
          <p>Your cart is empty</p>
          <a href="${window.Shopify?.routes?.root || '/'}collections/all" class="btn btn--primary btn--sm">Shop Now</a>
        </div>
      `;
    }
  };

  let cartToastTimer;

  const ensureCartToast = () => {
    let toast = $('[data-cart-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.setAttribute('data-cart-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <button type="button" class="cart-toast__close" data-cart-toast-close aria-label="Close">×</button>
      <div class="cart-toast__header">Added to cart</div>
      <div class="cart-toast__body">
        <div class="cart-toast__media" data-cart-toast-image></div>
        <div class="cart-toast__details">
          <div class="cart-toast__title" data-cart-toast-title></div>
          <div class="cart-toast__meta">
            Qty: <span data-cart-toast-qty></span>
          </div>
        </div>
      </div>
      <div class="cart-toast__summary">
        <div>Items in cart: <span data-cart-toast-count></span></div>
        <div>Total: <span data-cart-toast-total></span></div>
      </div>
      <div class="cart-toast__actions">
        <a href="${window.Shopify?.routes?.root || '/'}cart" class="btn btn--secondary btn--sm">View cart</a>
        <button type="button" class="btn btn--primary btn--sm" data-cart-toast-continue>Continue shopping</button>
      </div>
    `;
    document.body.appendChild(toast);

    toast.querySelector('[data-cart-toast-close]')?.addEventListener('click', () => hideCartToast());
    toast.querySelector('[data-cart-toast-continue]')?.addEventListener('click', () => hideCartToast());

    return toast;
  };

  const hideCartToast = () => {
    const toast = $('[data-cart-toast]');
    if (!toast) return;
    toast.classList.remove('is-visible');
  };

  const showCartToast = async (item, cartData) => {
    const toast = ensureCartToast();
    if (!toast || !item) return;

    let cart = cartData;
    if (!cart) {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        if (cartResponse.ok) {
          cart = await cartResponse.json();
        }
      } catch (err) {
        // ignore
      }
    }

    const title = escapeHtml(item.product_title || item.title || 'Item');
    const imageUrl = item.image || item.featured_image?.url || '';
    const quantity = item.quantity || 1;
    const count = cart ? cart.item_count : quantity;
    const total = cart ? cart.total_price : (item.final_line_price || (item.price || 0) * quantity);

    const imageWrapper = toast.querySelector('[data-cart-toast-image]');
    if (imageWrapper) {
      imageWrapper.innerHTML = imageUrl
        ? `<img src="${imageUrl}" alt="${title}" width="56" height="56" loading="lazy">`
        : '';
    }

    toast.querySelector('[data-cart-toast-title]').textContent = title;
    toast.querySelector('[data-cart-toast-qty]').textContent = quantity;
    toast.querySelector('[data-cart-toast-count]').textContent = count;
    toast.querySelector('[data-cart-toast-total]').textContent = formatMoney(total);

    toast.classList.add('is-visible');
    clearTimeout(cartToastTimer);
    cartToastTimer = setTimeout(() => {
      hideCartToast();
    }, 4000);
  };

  window.AquaCart = {
    formatMoney: formatMoney,
    updateCount: updateCartCountEls,
    updateMini: updateCartMini,
    showToast: showCartToast
  };

  // ==========================================================================
  // CART MINI (MOBILE TOGGLE)
  // ==========================================================================
  class CartMiniToggle {
    constructor() {
      this.mini = $('[data-cart-mini]');
      this.toggle = $('[data-cart-mini-toggle]');
      this.closeEls = this.mini ? $$('[data-cart-mini-close]', this.mini) : [];

      if (!this.mini || !this.toggle) return;
      this.bindEvents();
    }

    isMobile() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    bindEvents() {
      this.toggle.addEventListener('click', (e) => {
        if (!this.isMobile()) return;
        e.preventDefault();
        this.toggleOpen();
      });

      this.closeEls.forEach(el => {
        el.addEventListener('click', (e) => {
          if (!this.isMobile()) return;
          e.preventDefault();
          this.close();
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      window.addEventListener('resize', () => {
        if (!this.isMobile()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.mini.classList.contains('is-open');
    }

    open() {
      this.mini.classList.add('is-open');
    }

    close() {
      this.mini.classList.remove('is-open');
    }

    toggleOpen() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  new CartMiniToggle();

  // ==========================================================================
  // ADD TO CART
  // ==========================================================================
  class AddToCart {
    constructor(form) {
      this.form = form;
      this.submitBtn = $('[type="submit"]', form);
      this.originalBtnText = this.submitBtn?.textContent;

      this.bindEvents();
    }

    bindEvents() {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    async handleSubmit(e) {
      e.preventDefault();

      if (this.submitBtn.disabled) return;

      this.setLoading(true);

      try {
        const formData = new FormData(this.form);
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const item = await response.json();

        let cartData = null;
        try {
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          if (cartResponse.ok) {
            cartData = await cartResponse.json();
          }
        } catch (err) {
          console.warn('Cart fetch failed:', err);
        }

        if (cartData) {
          updateCartCountEls(cartData.item_count);
          updateCartMini(cartData);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: item }));
        }

        showCartToast(item, cartData);

        // Open cart drawer if enabled
        const cartDrawer = $('#cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.add('is-open');
          $('.cart-drawer__overlay')?.classList.add('is-visible');
          document.body.style.overflow = 'hidden';
        }

        this.setSuccess();
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.setError();
      }
    }

    setLoading(loading) {
      this.submitBtn.disabled = loading;
      if (loading) {
        this.submitBtn.innerHTML = '<span class="loading-spinner"></span>';
      }
    }

    setSuccess() {
      this.submitBtn.textContent = 'Added!';
      setTimeout(() => {
        this.submitBtn.textContent = this.originalBtnText;
        this.submitBtn.disabled = false;
      }, 2000);
    }

    setError() {
      this.submitBtn.textContent = 'Error - Try Again';
      this.submitBtn.disabled = false;
      setTimeout(() => {
        this.submitBtn.textContent = this.originalBtnText;
      }, 2000);
    }
  }

  $$('form[action*="/cart/add"]').forEach(form => new AddToCart(form));

  // ==========================================================================
  // ANNOUNCEMENT BAR
  // ==========================================================================
  class AnnouncementBar {
    constructor() {
      this.bar = $('.announcement-bar');
      this.closeBtn = $('.announcement-bar__close');
      this.storageKey = 'announcement_dismissed';

      if (!this.bar) return;

      this.checkDismissed();
      this.bindEvents();
    }

    bindEvents() {
      this.closeBtn?.addEventListener('click', () => this.dismiss());
    }

    checkDismissed() {
      const dismissed = sessionStorage.getItem(this.storageKey);
      if (dismissed) {
        this.bar.style.display = 'none';
      }
    }

    dismiss() {
      this.bar.style.display = 'none';
      sessionStorage.setItem(this.storageKey, 'true');
    }
  }

  new AnnouncementBar();

  // ==========================================================================
  // LAZY LOADING IMAGES
  // ==========================================================================
  if ('IntersectionObserver' in window) {
    const lazyImages = $$('img[data-src]');

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
          }
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px'
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  }

  // ==========================================================================
  // COUNTDOWN TIMER
  // ==========================================================================
  class CountdownTimer {
    constructor(element) {
      this.element = element;
      this.endDate = new Date(element.dataset.endDate).getTime();

      if (isNaN(this.endDate)) return;

      this.daysEl = $('[data-countdown-days]', element);
      this.hoursEl = $('[data-countdown-hours]', element);
      this.minutesEl = $('[data-countdown-minutes]', element);
      this.secondsEl = $('[data-countdown-seconds]', element);

      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    update() {
      const now = new Date().getTime();
      const distance = this.endDate - now;

      if (distance < 0) {
        clearInterval(this.interval);
        this.element.style.display = 'none';
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, '0');
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }

  $$('[data-countdown]').forEach(el => new CountdownTimer(el));

  // ==========================================================================
  // PRODUCT VARIANT SELECTOR
  // ==========================================================================
  class VariantSelector {
    constructor(container) {
      this.container = container;
      this.productForm = container.closest('form');

      // Guard clause - exit if no form found
      if (!this.productForm) {
        console.warn('VariantSelector: No form found for', container);
        return;
      }

      this.variantInput = $('[name="id"]', this.productForm);
      this.priceEl = $('[data-product-price]', container);
      this.comparePriceEl = $('[data-product-compare-price]', container);
      this.addToCartBtn = $('[type="submit"]', this.productForm);

      this.productData = JSON.parse($('[data-product-json]', container)?.textContent || '{}');

      this.bindEvents();
    }

    bindEvents() {
      $$('input[type="radio"], select', this.container).forEach(input => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      const selectedOptions = this.getSelectedOptions();
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.updatePrice(variant);
        this.updateButton(variant);
        this.updateVariantInput(variant);
        this.updateUrl(variant);
      }
    }

    getSelectedOptions() {
      const options = [];
      $$('[data-option-position]', this.container).forEach(group => {
        const selected = $('input:checked, select', group);
        if (selected) {
          options.push(selected.value);
        }
      });
      return options;
    }

    findVariant(options) {
      return this.productData.variants?.find(variant => {
        return options.every((option, index) => variant.options[index] === option);
      });
    }

    updatePrice(variant) {
      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }
      if (this.comparePriceEl) {
        if (variant.compare_at_price > variant.price) {
          this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = 'inline';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }
    }

    updateButton(variant) {
      if (!this.addToCartBtn) return;

      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = 'Add to Cart';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = 'Sold Out';
      }
    }

    updateVariantInput(variant) {
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }
    }

    updateUrl(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return window.Shopify?.currency?.active
        ? `${window.Shopify.currency.active} ${amount}`
        : `$${amount}`;
    }
  }

  $$('[data-variant-selector]').forEach(el => new VariantSelector(el));

  // ==========================================================================
  // BACK TO TOP BUTTON
  // ==========================================================================
  const backToTop = $('#back-to-top');
  if (backToTop) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 500) {
        backToTop.classList.add('is-visible');
      } else {
        backToTop.classList.remove('is-visible');
      }
    });

    backToTop.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ==========================================================================
  // NEWSLETTER FORM
  // ==========================================================================
  $$('.newsletter-form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      // Let Shopify handle the form submission
      // This is just for adding loading state
      const btn = $('[type="submit"]', form);
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<span class="loading-spinner"></span>';
      }
    });
  });

  // ==========================================================================
  // INITIALIZE ON DOM READY
  // ==========================================================================
  window.theme = {
    QuantitySelector,
    MobileMenu,
    CartDrawer,
    AddToCart,
    VariantSelector
  };

})();
